import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {AuthService} from "../../services/auth.service";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {UpdatePasswordDto} from "../../models/User";
import {takeRecoveryToken} from "../../utils/recovery-token.util";
import {Subscription} from "rxjs";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {NgClass} from "@angular/common";

@Component({
  selector: "app-reset-password-form",
  templateUrl: "./reset-password-form.component.html",
  styleUrls: ["./reset-password-form.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    TranslateModule,
  ],
})
export class ResetPasswordFormComponent implements OnInit, OnDestroy {
  loading = true;
  resetToken!: string;
  isTokenValid = false;
  resetPasswordForm: FormGroup;
  passwordChanged = false;
  private readonly userLanguage = navigator.language.startsWith("fr")
    ? "fr"
    : "en";
  public showNewPassword = false;
  public showNewConfirmPassword = false;
  error?: CustomError;
  private readonly subscriptions: Subscription[] = [];
  private verificationSubscription?: Subscription;
  private updatePasswordSubscription?: Subscription;
  public submitButtonText?: string;

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.resetPasswordForm = this.formBuilder.group({
      password: ["", Validators.required],
      confirm_password: ["", Validators.required],
    });
  }

  ngOnInit() {
    this.translate.defaultLang = this.userLanguage;
    this.translate.reloadLang(this.userLanguage);
    this.translate.use(this.userLanguage);
    this.setSubmitText("RESET_PASSWORD");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("RESET_PASSWORD");
      this.cdr.markForCheck();
    });

    const routeSubscription = this.route.params.subscribe((params) => {
      this.verificationSubscription?.unsubscribe();
      this.updatePasswordSubscription?.unsubscribe();
      this.error = undefined;
      this.loading = true;
      this.isTokenValid = false;
      this.passwordChanged = false;
      this.resetToken = takeRecoveryToken("reset-password", params["token"]);

      this.verificationSubscription = this.authService
        .verifyResetToken(this.resetToken)
        .subscribe({
          next: () => {
            this.loading = false;
            this.isTokenValid = true;
            this.cdr.markForCheck();
          },
          error: (error: CustomError) => {
            this.loading = false;
            this.error = error;
            this.cdr.markForCheck();
          },
        });
    });
    this.subscriptions.push(translateSubscription, routeSubscription);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitButtonText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  onSubmit() {
    if (this.resetPasswordForm.invalid) {
      this.error = {
        error: {
          error: "Please fill all required fields",
        },
      };
      return;
    }

    if (
      this.resetPasswordForm.value.password !==
      this.resetPasswordForm.value.confirm_password
    ) {
      this.error = {
        error: {
          error: "Passwords do not match",
        },
      };
      return;
    }

    this.setSubmitText("RESETTING_PASSWORD");

    const userPasswordDto: UpdatePasswordDto = {
      password: this.resetPasswordForm.value.password,
      confirm_password: this.resetPasswordForm.value.confirm_password,
      resetToken: this.resetToken,
    };

    this.updatePasswordSubscription?.unsubscribe();
    this.updatePasswordSubscription = this.authService
      .updatePassword(userPasswordDto)
      .subscribe({
        next: () => {
          this.setSubmitText("RESET_PASSWORD");
          this.passwordChanged = true;
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          this.setSubmitText("RESET_PASSWORD");
          this.error = error;
          this.cdr.markForCheck();
        },
      });
  }

  onKeyUp(event: KeyboardEvent, type: string): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "showNewPassword") {
        this.showNewPassword = !this.showNewPassword;
      } else if (type === "showNewConfirmPassword") {
        this.showNewConfirmPassword = !this.showNewConfirmPassword;
      }
    }
  }

  ngOnDestroy() {
    this.verificationSubscription?.unsubscribe();
    this.updatePasswordSubscription?.unsubscribe();
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
